import { useContext, useEffect, useState } from "react";
import { AlertModalCheckIcon } from "../../../../assets";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AppLayout } from "../../../../containers/layout/app";
import { EditCurrencyExchangeRateWrapper } from "./styled";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import Cookies from "universal-cookie";
import { BaseButton } from "../../../../components/button/styled";
import { AppContext } from "../../../../context/appContext";
import { updateCurrencyExchangeRateService } from "../../../../utils/api/configuration/currencyexchangerate/updateCurrencyExchangeRate";
import { retrieveCurrencyExchangeRateService } from "../../../../utils/api/configuration/currencyexchangerate/retrieveCurrencyExchangeRate";
import { computeExchangeAmountService } from "../../../../utils/api/configuration/currencyexchangerate/computeAmount";

export const EditCurrencyExchangeRate = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const options = ["buy", "sell"];
    const currencies = [
        { id: "NGN", name: "naira", abbreviation: "NGN" },
        { id: "CAD", name: "canadian dollar", abbreviation: "CAD" }
    ] as Record<string, any>[];

    const navigate = useNavigate();
    const { id = "" } = useParams();
    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [institutionId, setInstitutionId] = useState<string | null>(null);
    const [formDetails, setFormDetails] = useState({
        currencyFrom: " ",
        currencyTo: " ",
        spread: "",
        option: " ",
    });
    const [computedAmount, setComputedAmount] = useState(0);

    useEffect(() => {
        setActiveNavItem("Configuration");
    }, [setActiveNavItem]);

    useEffect(() => {
        if (!id) return;
        retrieveCurrencyExchangeRateService(TOKEN, id)
            .then((data) => {
                setInstitutionId(data.institution);
                setFormDetails({
                    ...data,
                    currencyFrom: data.from,
                    currencyTo: data.to,
                });
            })
            .catch((err) => {
                console.error("Failed to fetch currency exchange rate:", err);
            });
    }, [TOKEN, id]);

    useEffect(() => {
        if (formDetails.currencyFrom && formDetails.currencyTo && formDetails.spread) {
            const fetchComputedAmount = async () => {
                try {
                    const response = await computeExchangeAmountService(TOKEN, 1, formDetails.currencyFrom, formDetails.currencyTo, formDetails.spread);
                    return setComputedAmount(response.computedAmount);
                } catch (error) {
                    console.error('API fetch error:', error);
                }
            };
            fetchComputedAmount();
        }
    }, [TOKEN, formDetails.currencyFrom, formDetails.currencyTo, formDetails.spread]);

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        if (!institutionId) return;
        try {
            const response = await updateCurrencyExchangeRateService(TOKEN, institutionId, id, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to add currency exchange rate. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to add currency exchange rate. ${error.message}`);
            console.error('Failed to add currency exchange rate:', error);
        }
    };

    return (
        <AppLayout
            pageId={"Currency-Exchange-Rate"}
            pageTitle={"Edit Currency Exchange Rate"}
        >
            <EditCurrencyExchangeRateWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Update Currency Exchange Rate Successful"
                    message="Your information has been successfully added to the system."
                    callToAction="Back to Currency Exchange Rates List"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required Information to update a currency exchange rate
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Buy/Sell
                                </BaseLabel>
                                <BaseSelect
                                    name="option"
                                    readOnly
                                    fontweight={400}
                                    value={formDetails.option}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select Option
                                    </BaseOption>
                                    {options?.map((option: string, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={option}
                                            >
                                                {option.charAt(0).toUpperCase() + option.slice(1)}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Spread
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="spread"
                                    value={formDetails.spread}
                                    onChange={(e) => handleChange(e)}
                                    placeholder="Enter Amount"
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Currency (From)
                                </BaseLabel>
                                <BaseSelect
                                    readOnly
                                    name="currencyFrom"
                                    fontweight={400}
                                    value={formDetails.currencyFrom}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select Currency
                                    </BaseOption>
                                    {currencies?.map((currency: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={currency?.id}
                                            >
                                                {currency?.abbreviation}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Currency (To)
                                </BaseLabel>
                                <BaseSelect
                                    readOnly
                                    name="currencyTo"
                                    fontweight={400}
                                    value={formDetails.currencyTo}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select Currency
                                    </BaseOption>
                                    {currencies?.map((currency: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={currency?.id}
                                            >
                                                {currency?.abbreviation}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <Box
                                component={"div"}
                                className="unit-exchange-rate-box"
                            >
                                <Box>
                                    <Typography
                                        fontFamily={"Poppins"}
                                        fontWeight={"500"}
                                        fontSize={16}
                                        lineHeight={"normal"}
                                        color={"var(--dark-color)"}
                                        whiteSpace={"normal"}
                                        marginBlockEnd={"calc(var(--basic-margin)/2)"}
                                    >
                                        {`${formDetails.currencyFrom} → ${formDetails.currencyTo}`}
                                    </Typography>
                                </Box>
                                <Stack
                                    direction={{ miniTablet: "row" }}
                                    gap={{ miniTablet: "calc(var(--flex-gap)/8)" }}
                                >
                                    <Box>
                                        <Typography
                                            fontFamily={"Poppins"}
                                            fontWeight={"500"}
                                            fontSize={16}
                                            lineHeight={"normal"}
                                            color={"var(--dark-color)"}
                                            whiteSpace={"normal"}
                                        >
                                            Display Amount:
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            fontFamily={"Poppins"}
                                            fontWeight={"500"}
                                            fontSize={16}
                                            lineHeight={"normal"}
                                            color={"var(--dark-color)"}
                                            whiteSpace={"normal"}
                                        >
                                            {computedAmount}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid2>
                    </Grid2>
                    <Box
                        overflow={"hidden"}
                    >
                        {error && <Typography
                            fontFamily={"Inter"}
                            fontWeight={"600"}
                            fontSize={14}
                            lineHeight={"normal"}
                            color={"var(--text-salmon-pink-color)"}
                            whiteSpace={"normal"}
                            marginBlockEnd={"var(--basic-margin)"}
                        >
                            {error}
                        </Typography>}
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Update
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </EditCurrencyExchangeRateWrapper >
        </AppLayout >
    )
}